import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { subscribeToAllOrders, subscribeToNewsletterSignups, type NewsletterSignup, type Order } from '../../../utils/firebase';
import { ArrowRight, DollarSign, Mail, Package, Clock, TrendingUp } from 'lucide-react';
const DATA_TIMEOUT_MS = 10000;
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  loading
}: {
  label: string;
  value: string;
  hint: string;
  icon: typeof DollarSign;
  loading: boolean;
}) {
  return <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-102" data-mp="1dn3xxzlon2cu">
      <div className="flex items-start justify-between mb-4" data-id="element-103" data-mp="18r6l439moe4t">
        <p className="text-sm font-medium text-[#5A5A5A]" data-id="element-104" data-mp="oiziuwx5wv4c">{label}</p>
        <span className="w-9 h-9 rounded-lg bg-[#F0F4F8] flex items-center justify-center shrink-0" data-id="element-105" data-mp="sle39lghc2xq">
          <Icon className="w-4 h-4 text-tealslate" data-id="element-106" data-mp="em4ngmw6raua" />
        </span>
      </div>
      {loading ? <div className="h-9 w-24 rounded bg-[#F0F0F0] animate-pulse" data-id="element-107" data-mp="jkpmiizpboh5" /> : <p className="text-3xl font-bold text-[#111111]" data-id="element-108" data-mp="jmsl3rex6pxh">{value}</p>}
      <p className="mt-1 text-xs text-[#9A9A90]" data-id="element-109" data-mp="17bbu695sn6qm">{hint}</p>
    </div>;
}
export function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const [orders, setOrders] = useState<Order[]>([]);
  const [signups, setSignups] = useState<NewsletterSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const linkTo = (path: string) => isPreview ? `${path}?preview=true` : path;
  useEffect(() => {
    // Firestore reads can hang when rules block them, so the overview always
    // resolves to a readable state rather than an endless spinner.
    const timer = setTimeout(() => {
      setLoading(false);
      setLoadFailed(true);
    }, DATA_TIMEOUT_MS);
    const unsubOrders = subscribeToAllOrders(data => {
      clearTimeout(timer);
      setOrders(data);
      setLoading(false);
      setLoadFailed(false);
    });
    const unsubSignups = subscribeToNewsletterSignups(data => {
      setSignups(data);
    });
    return () => {
      clearTimeout(timer);
      unsubOrders();
      unsubSignups();
    };
  }, []);
  const stats = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    let totalRevenue = 0;
    let ordersToday = 0;
    let pending = 0;
    orders.forEach(order => {
      if (order.status === 'paid') {
        totalRevenue += order.total;
        if (new Date(order.createdAt) >= startOfToday) ordersToday++;
      }
      if (order.fulfillmentStatus === 'pending') pending++;
    });
    return {
      totalRevenue,
      ordersToday,
      pending,
      totalOrders: orders.length
    };
  }, [orders]);
  const recentOrders = useMemo(() => [...orders].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5), [orders]);
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-110" data-mp="2v5c9cjqp7qcq">
      <header className="mb-8" data-id="element-111" data-mp="2v414egliock1">
        <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-2" data-id="element-112" data-mp="305goo6fvef1d">
          {today}
        </p>
        <h1 className="text-3xl font-bold text-[#111111] tracking-tight" data-id="element-113" data-mp="34w6shf8ne9ty">
          Dashboard
        </h1>
        <p className="mt-2 text-sm text-[#5A5A5A]" data-id="element-114" data-mp="27fhw412gvzry">
          A snapshot of store activity, fulfillment, and audience growth.
        </p>
      </header>

      {loadFailed && <div role="status" className="mb-8 bg-white border border-[#E5E5E5] border-l-4 border-l-[#D97706] rounded-lg p-4" data-id="element-115" data-mp="iwdondzmaswu">
          <p className="text-sm font-bold text-[#111111] mb-1" data-id="element-116" data-mp="dzn86pjwhvaw">
            Showing empty figures — live data unavailable
          </p>
          <p className="text-sm text-[#5A5A5A]" data-id="element-117" data-mp="myqus3ia0su5">
            {isPreview ? 'You are viewing the admin panel in preview mode without signing in, so Firestore is not returning store data. The layout below is fully functional and will populate once an admin signs in.' : 'Store data could not be read from Firestore. Security rules may be blocking access to the orders collection.'}
          </p>
        </div>}

      {/* Key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-118" data-mp="16kdembp3ufad">
        <StatCard label="Total revenue" value={formatPrice(stats.totalRevenue)} hint="All paid orders" icon={DollarSign} loading={loading} data-id="element-119" data-mp="3cfy72ulsnx32" />
        <StatCard label="Orders today" value={String(stats.ordersToday)} hint="Since midnight" icon={TrendingUp} loading={loading} data-id="element-120" data-mp="2yafrljahk1wr" />
        <StatCard label="Awaiting fulfillment" value={String(stats.pending)} hint="Pending shipment" icon={Clock} loading={loading} data-id="element-121" data-mp="2ojbekp4der4k" />
        <StatCard label="Subscribers" value={String(signups.length)} hint="Newsletter signups" icon={Mail} loading={loading} data-id="element-122" data-mp="2el5dg9qe1p0r" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-123" data-mp="25ma44os1ryt8">
        {/* Recent orders */}
        <section className="lg:col-span-2 bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden" data-id="element-124" data-mp="2ald25p9m14rp">
          <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between bg-[#FAFAFA]" data-id="element-125" data-mp="3v6sbw6f8e73">
            <h2 className="font-bold text-[#111111]" data-id="element-126" data-mp="8s72ai6e613h">Recent orders</h2>
            <Link to={linkTo('/admin/orders')} className="text-sm font-bold text-tealslate hover:underline inline-flex items-center gap-1" data-id="element-127" data-mp="6fcxg76ruap8">
              View all <ArrowRight className="w-3.5 h-3.5" data-id="element-128" data-mp="3ti80b2k2d31r" />
            </Link>
          </div>

          {loading ? <ul className="divide-y divide-[#E5E5E5]" data-id="element-129" data-mp="3okycfrcbozg1">
              {[0, 1, 2].map(row => <li key={row} className="px-6 py-4" data-id="element-130" data-mp="q19b6e1rmogf">
                  <div className="h-4 w-40 rounded bg-[#F0F0F0] animate-pulse mb-2" data-id="element-131" data-mp="l37shu894r5h" />
                  <div className="h-3 w-24 rounded bg-[#F5F5F5] animate-pulse" data-id="element-132" data-mp="l5ap4x3wtij6" />
                </li>)}
            </ul> : recentOrders.length === 0 ? <div className="px-6 py-14 text-center" data-id="element-133" data-mp="bcv7y20fsxzc">
              <Package className="w-8 h-8 text-[#C9C9C9] mx-auto mb-3" data-id="element-134" data-mp="18tah20cyjezc" />
              <p className="text-sm font-bold text-[#111111] mb-1" data-id="element-135" data-mp="18ulms5pyqwt3">
                No orders yet
              </p>
              <p className="text-sm text-[#5A5A5A] max-w-sm mx-auto" data-id="element-136" data-mp="1bdgju4pu4zeb">
                Once a customer completes a purchase, it will appear here with
                its fulfillment status.
              </p>
            </div> : <ul className="divide-y divide-[#E5E5E5]" data-id="element-137" data-mp="1lal9wfmsxf0e">
              {recentOrders.map(order => <li key={order.id} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-[#FAFAFA] transition-colors" data-id="element-138" data-mp="1qb8y6tjnbhqx">
                  <div className="min-w-0" data-id="element-139" data-mp="1za46qca5x3xe">
                    <Link to={linkTo(`/admin/orders/${order.id}`)} className="text-sm font-bold text-tealslate hover:underline" data-id="element-140" data-mp="1z8t106x5pm3n">
                      #{order.id.slice(0, 8).toUpperCase()}
                    </Link>
                    <p className="text-xs text-[#5A5A5A] truncate" data-id="element-141" data-mp="2e1wiyfenu1uw">
                      {order.email} · {formatDate(order.createdAt)}
                    </p>
                  </div>
                  <div className="text-right shrink-0" data-id="element-142" data-mp="33baqcjwyw8q5">
                    <p className="text-sm font-bold text-[#111111]" data-id="element-143" data-mp="2zba6ukv0h7c8">
                      {formatPrice(order.total)}
                    </p>
                    <span className="text-xs text-[#5A5A5A] capitalize" data-id="element-144" data-mp="3da9lr0sq27v7">
                      {order.fulfillmentStatus.replace('_', ' ')}
                    </span>
                  </div>
                </li>)}
            </ul>}
        </section>

        {/* Quick actions */}
        <section className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden" data-id="element-145" data-mp="2fom1fe9jjg01">
          <div className="px-6 py-4 border-b border-[#E5E5E5] bg-[#FAFAFA]" data-id="element-146" data-mp="2udi6r3k7qewy">
            <h2 className="font-bold text-[#111111]" data-id="element-147" data-mp="2pk5u7rst5ykk">Quick actions</h2>
          </div>
          <div className="p-4 space-y-2" data-id="element-148" data-mp="21v36c27kz9hm">
            <Link to={linkTo('/admin/orders')} className="flex items-start gap-3 p-4 rounded-lg border border-[#E5E5E5] hover:border-tealslate transition-colors group" data-id="element-149" data-mp="21ts1dz2efvox">
              <Package className="w-5 h-5 text-tealslate shrink-0 mt-0.5" data-id="element-150" data-mp="cced6m0xbzf9" />
              <span data-id="element-151" data-mp="h6sboxavumo5">
                <span className="block text-sm font-bold text-[#111111] group-hover:text-tealslate" data-id="element-152" data-mp="h7ahakjfqd8q">
                  Manage orders
                </span>
                <span className="block text-xs text-[#5A5A5A] mt-0.5" data-id="element-153" data-mp="7gftvcugd7lp">
                  Search, filter, and update fulfillment.
                </span>
              </span>
            </Link>
            <Link to={linkTo('/admin/newsletter')} className="flex items-start gap-3 p-4 rounded-lg border border-[#E5E5E5] hover:border-tealslate transition-colors group" data-id="element-154" data-mp="104bmo6gwifd7">
              <Mail className="w-5 h-5 text-tealslate shrink-0 mt-0.5" data-id="element-155" data-mp="r2c1d1yusp4g" />
              <span data-id="element-156" data-mp="36u01f695yxrh">
                <span className="block text-sm font-bold text-[#111111] group-hover:text-tealslate" data-id="element-157" data-mp="36thspa5ber2o">
                  Newsletter subscribers
                </span>
                <span className="block text-xs text-[#5A5A5A] mt-0.5" data-id="element-158" data-mp="1boqacdrrkp7l">
                  Review signups and export the list.
                </span>
              </span>
            </Link>
            <Link to="/" className="flex items-start gap-3 p-4 rounded-lg border border-[#E5E5E5] hover:border-tealslate transition-colors group" data-id="element-159" data-mp="1frwg7kaysedj">
              <ArrowRight className="w-5 h-5 text-tealslate shrink-0 mt-0.5" data-id="element-160" data-mp="16vng03f5xbqc" />
              <span data-id="element-161" data-mp="ryxsjmiocvtn">
                <span className="block text-sm font-bold text-[#111111] group-hover:text-tealslate" data-id="element-162" data-mp="s2tykcgbylj1">
                  View storefront
                </span>
                <span className="block text-xs text-[#5A5A5A] mt-0.5" data-id="element-163" data-mp="j146r5ijdkkq">
                  Open the live RD Ready site.
                </span>
              </span>
            </Link>
          </div>
        </section>
      </div>
    </div>;
}